/**
 * ObjectPage — the one scaffold every commitment (and party) page instantiates
 * (Page Templates §3; Wireframes §2–6). One canonical page per object,
 * regardless of kind: learn one object page, you've learned them all. Kinds
 * extend *within* the layout (the `extensions` slot); they never add a
 * top-level section (Principle 10).
 *
 * Graduated from the /styleguide/object-page prototype — the layout is:
 *
 *   PageHeader (flush)          breadcrumb · title + state · essence · actions
 *   ObjectKeyFacts band         Owner + the kind's vitals (the `keyFacts` slot)
 *   layers                      cross-cutting region (the Customer Dossier)
 *   ┌── main (2/3) ───────────┬── rail (1/3) ──────────────┐
 *   │ summary                 │ recommendations            │
 *   │ extensions (kind board) │ actions                    │
 *   │ timeline                │ commitments · relationships │
 *   │                         │ notes                      │
 *   └─────────────────────────┴────────────────────────────┘
 *
 * Layout rules it locks: responsive single-column stack < lg (OQ-15); an empty
 * slot collapses (no dead gray box) — the page passes a guiding affordance for
 * the slots that should always offer a next step (timeline, notes). Each slot is
 * a self-contained Card; this block owns the grid, not the section chrome.
 *
 * Context objects (Transaction/Note/Attachment — no lifecycle) do NOT use this;
 * they open in the lighter Detail drawer (C6). ObjectPage is for commitments +
 * parties only. Every new prop is OPTIONAL, so existing callers are unchanged.
 */

import * as React from "react";
import { PageHeader, type PageHeaderCrumb } from "./page-header";
import { IntentBadge } from "./status-badge";
import { Card, CardContent } from "../card";
import { lifecycleIntent, type Intent } from "../tokens";
import { cn } from "../utils";

const humanize = (s: string) =>
  s.replace(/[_-]+/g, " ").replace(/^./, (c) => c.toUpperCase());

export interface ObjectPageProps {
  /** The object's title (the commitment/party name). */
  title: React.ReactNode;
  /** Kind label shown after the title (e.g. "Deal", "Task"). */
  kind?: string;
  /** Free-form lifecycle state; colored via the canonical `lifecycleIntent`. */
  state?: string;
  /** Override the rendered state text (else the state is humanized). */
  stateLabel?: string;
  /** Override the state badge intent (e.g. customer `at_risk`/`churned`). */
  stateIntent?: Intent;
  /** Eyebrow above the title — used when no breadcrumb trail is supplied. */
  eyebrow?: React.ReactNode;
  /** Breadcrumb trail above the title (the canonical PageHeader breadcrumbs). */
  breadcrumbs?: PageHeaderCrumb[];
  /** One-line essence under the title (e.g. "Task · awaiting verify"). */
  subtitle?: React.ReactNode;
  /** Header-right actions — the Verify ▾ / ⋯ cluster. */
  headerActions?: React.ReactNode;

  /** The key-facts band (Owner + the kind's vitals) — an `<ObjectKeyFacts>`. */
  keyFacts?: React.ReactNode;

  /** Section 1 — the commitment's fields + kind extension (required). */
  summary: React.ReactNode;
  /**
   * Cross-cutting layers region (Customer Module §4.1) — rendered full-width
   * between the key-facts band and the body grid (e.g. the Dossier's lens
   * cards). Collapses entirely when absent.
   */
  layers?: React.ReactNode;
  /** Kind extensions in the MAIN column (goal issue-board, deal pipeline, …). */
  extensions?: React.ReactNode;
  /** Section 5 — Play scoped to this object (rail). */
  recommendations?: React.ReactNode;
  /** Section 6 — available transitions + Verify/Delegate (rail). */
  actions?: React.ReactNode;
  /** Section 2 — the event stream (main column). */
  timeline?: React.ReactNode;
  /** Section 3 — sub/parent + dependencies (rail). */
  commitments?: React.ReactNode;
  /** Section 4 — entity_links graph slice (rail). */
  relationships?: React.ReactNode;
  /** A rail Notes slot — typically a guiding "Add the first note" empty. */
  notes?: React.ReactNode;

  className?: string;
}

export function ObjectPage({
  title,
  kind,
  state,
  stateLabel,
  stateIntent,
  eyebrow,
  breadcrumbs,
  subtitle,
  headerActions,
  keyFacts,
  summary,
  layers,
  extensions,
  recommendations,
  actions,
  timeline,
  commitments,
  relationships,
  notes,
  className,
}: ObjectPageProps) {
  return (
    <div className={cn("flex flex-col gap-5", className)}>
      <PageHeader
        breadcrumbs={breadcrumbs}
        eyebrow={breadcrumbs?.length ? undefined : eyebrow}
        title={title}
        titleAccessory={state ? (
          <IntentBadge intent={stateIntent ?? lifecycleIntent(state)} withDot>
            {stateLabel ?? humanize(state)}
          </IntentBadge>
        ) : undefined}
        subtitle={
          subtitle ??
          (kind ? (
            <span className="text-muted-foreground">{humanize(kind)}</span>
          ) : undefined)
        }
        toolbar={headerActions}
      />

      {keyFacts}

      {/* Cross-cutting layers (Customer Module §4.1) — only when provided. */}
      {layers}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* main column */}
        <div className="flex flex-col gap-4 lg:col-span-2">
          {summary}
          {extensions}
          {timeline}
        </div>

        {/* context rail */}
        <div className="flex flex-col gap-4">
          {recommendations}
          {actions}
          {commitments}
          {relationships}
          {notes}
        </div>
      </div>
    </div>
  );
}
